import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, BookOpen, SlidersHorizontal, Trash2, Wrench, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useApplications } from '../../../hooks/use-applications';
import { AppAvatarEditor } from '../../../ui/AppAvatarEditor';
import { useSkills } from '../../skills/hooks/use-skills';
import { useTools } from '../../tools/hooks/use-tools';
import type { CatalogItem } from '../AgentRefsBindingPanel';
import { uploadAgentAvatar } from '../agentApi';
import { useModelRegistry } from '../hooks/use-agents';
import type { Agent, UpdateAgentDto } from '../types';
import {
  CAPABILITIES,
  type CapabilityKey,
  CapabilityRow,
  ModelSelectPopover,
  modelCapabilityKeys,
} from './model-picker';
import { ToolSkillPickerDialog } from './ToolSkillPickerDialog';

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z
  .object({
    displayName: z.string().min(1, 'Required'),
    shortDescription: z.string(),
    type: z.enum(['TASK', 'CONVERSATIONAL']),
    scope: z.enum(['PUBLIC', 'APPLICATION']),
    appSlug: z.string().nullable().optional(),
    modelAlias: z.string().min(1, 'Required'),
    requiredCapabilities: z.array(z.string()),
  })
  .superRefine((val, ctx) => {
    if (val.scope === 'APPLICATION' && !val.appSlug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['appSlug'],
        message: 'Required when scope is Application',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

function buildDefaults(agent: Agent): FormValues {
  const scope = agent.scope === 'TENANT' ? 'APPLICATION' : agent.scope;
  return {
    displayName: agent.interface.displayName,
    shortDescription: agent.interface.shortDescription,
    type: agent.type,
    scope,
    appSlug: scope === 'PUBLIC' ? null : (agent.appSlug ?? null),
    modelAlias: agent.modelAlias,
    requiredCapabilities: agent.guardrails?.requiredCapabilities ?? [],
  };
}

// ─── ConfigTab ────────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'CONVERSATIONAL', label: 'Conversational' },
  { value: 'TASK', label: 'Task' },
] as const;

const SCOPE_OPTIONS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'APPLICATION', label: 'Application' },
] as const;

export function ConfigTab({
  agent,
  onSave,
  isSaving = false,
  onDelete,
  isDeleting = false,
  isBehaviorLocked = false,
  onSaveRefs,
  isRefsUpdating = false,
}: {
  agent: Agent;
  onSave?: (dto: UpdateAgentDto) => Promise<void>;
  isSaving?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  isBehaviorLocked?: boolean;
  onSaveRefs?: (refs: { skillRefs?: string[]; toolRefs?: string[] }) => Promise<void>;
  isRefsUpdating?: boolean;
}) {
  const canEdit = Boolean(onSave);
  const canBehaviorEdit = canEdit && !isBehaviorLocked;
  const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  const [skillPickerOpen, setSkillPickerOpen] = useState(false);
  const { data: applications } = useApplications();
  const { data: registry = [] } = useModelRegistry();
  const appOptions = (applications ?? []).filter((a) => a.type === 'app' || a.type === 'portal');

  const { data: allSkills = [], isLoading: skillsLoading } = useSkills('_all');
  const { data: allTools = [], isLoading: toolsLoading } = useTools();

  const skillCatalog = useMemo(
    () =>
      allSkills.map((s) => ({
        value: s.name,
        label: s.interface.displayName || s.name,
        meta: s.appSlug,
        href: `/skills/${s.appSlug}/${s.name}`,
        scope: s.scope,
        description: s.interface.shortDescription,
        status: s.status,
      })),
    [allSkills],
  );

  const toolCatalog = useMemo(
    () =>
      allTools.map((t) => ({
        value: t.name,
        label: (t.interface as { displayName?: string }).displayName || t.name,
        meta: t.appSlug,
        href: `/tools/${t.id}`,
        scope: t.scope,
        description: t.description,
        status: t.status,
      })),
    [allTools],
  );

  // Group selected tools by name prefix (e.g. "ado" from "ado.get_workitem")
  const groupedTools = useMemo(() => {
    const groups = new Map<string, CatalogItem[]>();
    for (const ref of agent.toolRefs ?? []) {
      const item = toolCatalog.find((t) => t.value === ref) ?? { value: ref, label: ref };
      const prefix = ref.includes('.') ? ref.split('.')[0] : 'other';
      if (!groups.has(prefix)) groups.set(prefix, []);
      groups.get(prefix)?.push(item);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [agent.toolRefs, toolCatalog]);

  // Group selected skills by appSlug (meta)
  const groupedSkills = useMemo(() => {
    const groups = new Map<string, CatalogItem[]>();
    for (const ref of agent.skillRefs ?? []) {
      const item = skillCatalog.find((s) => s.value === ref) ?? {
        value: ref,
        label: ref,
        meta: undefined,
      };
      const group = item.meta ?? 'global';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)?.push(item);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [agent.skillRefs, skillCatalog]);

  const form = useForm<FormValues>({
    resolver: canEdit ? zodResolver(schema) : undefined,
    defaultValues: buildDefaults(agent),
    mode: 'onTouched',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when agent id changes
  useEffect(() => {
    form.reset(buildDefaults(agent));
    setPendingAvatar(null);
  }, [agent.id]);

  const watchedScope = form.watch('scope');
  const watchedCapabilities = form.watch('requiredCapabilities');
  const selectedCapabilities = useMemo(
    () => new Set(modelCapabilityKeys(watchedCapabilities)),
    [watchedCapabilities],
  );

  useEffect(() => {
    if (watchedScope === 'PUBLIC' && form.getValues('appSlug') !== null) {
      form.setValue('appSlug', null, { shouldDirty: true });
    }
  }, [watchedScope, form]);

  const capabilityFilteredModels = useMemo(() => {
    // Chat agents use chat-completion models only — exclude image generation models.
    const active = registry.filter((m) => m.isActive && !m.supportsImageGeneration);
    if (!selectedCapabilities.size) return active;
    return active.filter((m) => [...selectedCapabilities].every((cap) => m[cap] === true));
  }, [registry, selectedCapabilities]);

  const filteredProviders = useMemo(
    () =>
      [
        ...new Set(
          capabilityFilteredModels.map((m) => m.provider).filter((p): p is string => Boolean(p)),
        ),
      ].sort(),
    [capabilityFilteredModels],
  );

  // Clear modelAlias when capability filter removes its model from the list
  useEffect(() => {
    const current = form.getValues('modelAlias');
    if (!current) return;
    const stillValid = capabilityFilteredModels.some((m) => m.modelName === current);
    if (!stillValid) {
      form.setValue('modelAlias', '', { shouldDirty: true });
    }
  }, [capabilityFilteredModels, form]);

  function toggleCapability(key: CapabilityKey) {
    const current = form.getValues('requiredCapabilities');
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    form.setValue('requiredCapabilities', next, { shouldDirty: true });
  }

  const isDirty = form.formState.isDirty || pendingAvatar !== null;

  async function onSubmit(data: FormValues) {
    console.log('[ConfigTab] save payload', data);
    if (pendingAvatar) {
      setUploadingAvatar(true);
      try {
        await uploadAgentAvatar(agent.name, agent.appSlug, pendingAvatar);
        setPendingAvatar(null);
      } catch {
        toast.error('Failed to upload avatar');
      } finally {
        setUploadingAvatar(false);
      }
    }

    await onSave?.({
      type: data.type,
      scope: data.scope,
      modelAlias: data.modelAlias,
      interface: {
        ...agent.interface,
        displayName: data.displayName,
        shortDescription: data.shortDescription,
      },
      guardrails: {
        ...agent.guardrails,
        requiredCapabilities: data.requiredCapabilities,
      },
    });

    form.reset(data);
  }

  const saving = isSaving || uploadingAvatar;

  return (
    <Form {...form}>
      <FormLayout
        className="sm:pt-4"
        actionsPosition="sticky"
        isDirty={canEdit && isDirty}
        validationErrors={form.formState.errors as Record<string, { message?: string } | undefined>}
        cancelAction={{
          label: 'Discard',
          type: 'button',
          onClick: () => {
            form.reset(buildDefaults(agent));
            setPendingAvatar(null);
          },
        }}
        submitAction={{
          label: 'Save changes',
          type: 'button',
          disabled: saving || Object.keys(form.formState.errors).length > 0,
          onClick: () => {
            console.log('[ConfigTab] current values', form.getValues());
            void form.handleSubmit(onSubmit, (errors) =>
              console.log('[ConfigTab] validation errors', errors),
            )();
          },
        }}
      >
        <FormSection title="Identity" asCard>
          {canEdit && (
            <AppAvatarEditor
              logoUrl={agent.iconUrl}
              pendingFile={pendingAvatar}
              onFileSelect={setPendingAvatar}
              onClearImage={() => setPendingAvatar(null)}
              avatarIcon={null}
              onIconChange={() => {}}
              avatarBgColor={null}
              onBgColorChange={() => {}}
              fallback={(agent.interface.displayName || agent.name).slice(0, 2).toUpperCase()}
              title="Agent Avatar"
              enableIcon={false}
              aiGenerate={{
                agentAppSlug: agent.appSlug,
                agentName: agent.name,
                agentDescription: agent.interface.shortDescription,
              }}
            />
          )}

          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Name</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!canEdit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="shortDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Short Description</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={4}
                    className="resize-none"
                    disabled={!canEdit}
                    placeholder="What this agent does in one sentence"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection title="Agent Type" asCard>
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <FormControl>
                  <SegmentedControl
                    options={TYPE_OPTIONS}
                    value={field.value}
                    onValueChange={canEdit ? field.onChange : undefined}
                    className={!canEdit ? 'pointer-events-none opacity-70' : ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope</FormLabel>
                <FormControl>
                  <SegmentedControl
                    options={SCOPE_OPTIONS}
                    value={field.value}
                    onValueChange={canEdit ? field.onChange : undefined}
                    className={!canEdit ? 'pointer-events-none opacity-70' : ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {watchedScope === 'APPLICATION' && (
            <FormField
              control={form.control}
              name="appSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Application</FormLabel>
                  {canEdit ? (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                      disabled={appOptions.length === 0}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an application" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {appOptions.map((app) => (
                          <SelectItem key={app.slug} value={app.slug}>
                            {app.displayName || app.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-mono">{field.value || '—'}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </FormSection>

        <FormSection title="Model & Capabilities" asCard>
          {isBehaviorLocked && (
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Active deployment running
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  Model selection cannot be changed while there is an active deployment. Deactivate
                  the deployment first.
                </p>
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium mb-0.5">Required capabilities</p>
            <p className="text-xs text-muted-foreground mb-3">
              Select which capabilities this agent requires. The model list below will show only
              compatible models.
            </p>
            <div className="overflow-hidden rounded-xl border border-border">
              {CAPABILITIES.filter((cap) => cap.key !== 'supportsImageGeneration').map((cap) => (
                <CapabilityRow
                  key={cap.key}
                  cap={cap}
                  selected={selectedCapabilities.has(cap.key)}
                  onToggle={() => toggleCapability(cap.key)}
                  disabled={!canBehaviorEdit}
                />
              ))}
            </div>
          </div>

          <FormField
            control={form.control}
            name="modelAlias"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Default Model</FormLabel>
                {registry.length > 0 ? (
                  capabilityFilteredModels.length > 0 ? (
                    <FormControl>
                      <ModelSelectPopover
                        value={field.value || undefined}
                        onChange={(v) => field.onChange(v ?? '')}
                        models={capabilityFilteredModels}
                        providers={filteredProviders}
                        disabled={!canBehaviorEdit}
                      />
                    </FormControl>
                  ) : (
                    <p className="text-xs text-muted-foreground pt-1">
                      No models support all selected capabilities.
                    </p>
                  )
                ) : (
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. claude-sonnet-4-6"
                      disabled={!canBehaviorEdit}
                    />
                  </FormControl>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection title="Tools" asCard>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {(agent.toolRefs ?? []).length} tool{(agent.toolRefs ?? []).length !== 1 ? 's' : ''}{' '}
              enabled
            </p>
            {onSaveRefs && (
              <Button
                type="button"
                size="sm"
                appearance="outline"
                onClick={() => setToolPickerOpen(true)}
              >
                <SlidersHorizontal className="size-3.5 mr-1.5" />
                Manage Tools
              </Button>
            )}
          </div>

          {groupedTools.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center">
              <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                <Wrench className="size-4 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">No tools enabled</p>
              {onSaveRefs && (
                <Button
                  type="button"
                  size="sm"
                  appearance="ghost"
                  onClick={() => setToolPickerOpen(true)}
                >
                  Add tools
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {groupedTools.map(([prefix, items]) => (
                <div key={prefix}>
                  <p className="text-[11px] font-mono font-medium text-muted-foreground mb-1.5">
                    {prefix}.*
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((item) => (
                      <Badge
                        key={item.value}
                        variant="secondary"
                        appearance="soft"
                        className="font-mono text-xs gap-1.5 pr-1"
                      >
                        <Wrench className="size-3" />
                        {item.label !== item.value ? item.label : item.value}
                        {onSaveRefs && (
                          <button
                            type="button"
                            disabled={isRefsUpdating}
                            aria-label={`Remove ${item.value}`}
                            onClick={() =>
                              void onSaveRefs({
                                toolRefs: (agent.toolRefs ?? []).filter((r) => r !== item.value),
                              })
                            }
                            className="ml-0.5 rounded-full opacity-60 hover:opacity-100 disabled:pointer-events-none"
                          >
                            <X className="size-2.5" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <ToolSkillPickerDialog
            open={toolPickerOpen}
            onOpenChange={setToolPickerOpen}
            title="Tools"
            catalog={toolCatalog}
            catalogLoading={toolsLoading}
            selectedRefs={agent.toolRefs ?? []}
            isSaving={isRefsUpdating}
            onConfirm={async (newRefs) => {
              await onSaveRefs?.({ toolRefs: newRefs });
            }}
          />
        </FormSection>

        <FormSection title="Skills" asCard>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {(agent.skillRefs ?? []).length} skill
              {(agent.skillRefs ?? []).length !== 1 ? 's' : ''} available
            </p>
            {onSaveRefs && (
              <Button
                type="button"
                size="sm"
                appearance="outline"
                onClick={() => setSkillPickerOpen(true)}
              >
                <SlidersHorizontal className="size-3.5 mr-1.5" />
                Manage Skills
              </Button>
            )}
          </div>

          {groupedSkills.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center">
              <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                <BookOpen className="size-4 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">No skills assigned</p>
              {onSaveRefs && (
                <Button
                  type="button"
                  size="sm"
                  appearance="ghost"
                  onClick={() => setSkillPickerOpen(true)}
                >
                  Add skills
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {groupedSkills.map(([group, items]) => (
                <div key={group}>
                  <p className="text-[11px] font-mono font-medium text-muted-foreground mb-1.5">
                    {group}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((item) => (
                      <Badge
                        key={item.value}
                        variant="secondary"
                        appearance="soft"
                        className="font-mono text-xs gap-1.5 pr-1"
                      >
                        <BookOpen className="size-3" />
                        {item.label !== item.value ? item.label : item.value}
                        {onSaveRefs && (
                          <button
                            type="button"
                            disabled={isRefsUpdating}
                            aria-label={`Remove ${item.value}`}
                            onClick={() =>
                              void onSaveRefs({
                                skillRefs: (agent.skillRefs ?? []).filter((r) => r !== item.value),
                              })
                            }
                            className="ml-0.5 rounded-full opacity-60 hover:opacity-100 disabled:pointer-events-none"
                          >
                            <X className="size-2.5" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <ToolSkillPickerDialog
            open={skillPickerOpen}
            onOpenChange={setSkillPickerOpen}
            title="Skills"
            catalog={skillCatalog}
            catalogLoading={skillsLoading}
            selectedRefs={agent.skillRefs ?? []}
            isSaving={isRefsUpdating}
            onConfirm={async (newRefs) => {
              await onSaveRefs?.({ skillRefs: newRefs });
            }}
          />
        </FormSection>

        {onDelete && (
          <FormSection title="Danger Zone" asCard>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-medium">Delete agent</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Permanently removes this agent and all its releases. This cannot be undone.
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={isDeleting}
                    className="w-full sm:w-auto sm:shrink-0"
                  >
                    <Trash2 className="mr-1.5 size-3.5" />
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete {agent.interface.displayName || agent.name}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this agent and all its releases. Deployments
                      referencing this agent will stop working. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete agent
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </FormSection>
        )}
      </FormLayout>
    </Form>
  );
}
